/**
 * Product-group serving hours (Business Rule #52).
 *
 * A branch may carry PRODUCT GROUPS — Fresh Food Therapy at Gigaplex — whose
 * kitchen keeps different hours from the store. Each group has its own weekly
 * `operating_hours` (the same "0"-"6" → slots shape as the branch) evaluated
 * INSIDE the store's window: store closed wins, then a sold-out item, then a
 * closed group.
 *
 * Membership is the Rista ACCOUNT the item's sale lines are booked to
 * (`menu_items.rista_account`, learned server-side from the sales feed — the
 * catalogue never exposes it), plus a manual `parent_skus` include list for
 * items Rista hasn't sold yet and an `exclude_parent_skus` list that always
 * wins. The slim `GET /menu` list carries neither field, so the server stamps
 * members with `serving_group: <group id>` and this mirror honours that stamp
 * first; full docs (`/menu/{id}`, `/menu/items/batch`) match on the fields.
 *
 * Mirrors backend/services/product_group_hours.py — the two MUST stay in sync.
 * The client computes the window for DISPLAY (greyed cards, the cart banner,
 * the disabled CTA); `create_order` re-evaluates the SAME rule as the
 * hard-stop. A client mirror may inform the UI; only the server refuses a
 * sale — Checkout gates only on a server-confirmed status (see
 * `shouldBlockGroupLines`).
 *
 * Pure JS, no fetches. Time math is reused from storeHours.js (IST-anchored).
 */
import { nowInIst, parseHHMM, pyWeekday, slotsForWeekday, storeStatus } from './storeHours';

export const DISABLED = 'disabled';
export const NO_RESTRICTION = 'no_restriction';

// Customer-facing copy for a closed partner group (mirror of the backend
// GROUP_UNAVAILABLE_MESSAGE). Since 2026-09-16 a closed partner-group item is
// HIDDEN on the discovery surfaces while the store is open, so the serving
// window is never advertised — the block surfaces (cart line, direct product
// link) just say it isn't available. An admin `closed_message` still overrides.
export const GROUP_UNAVAILABLE_MESSAGE = "This item isn't available right now.";
// Short label for the tight spots (a disabled Add button, an image badge).
export const GROUP_UNAVAILABLE_SHORT = 'Not available';

const normAccount = (v) => String(v || '').split(/\s+/).filter(Boolean).join(' ').toLowerCase();

function normSkus(values) {
  const out = [];
  for (const v of values || []) {
    const s = String(v || '').trim().toUpperCase();
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
}

/**
 * Does this menu row belong to the group? Membership only — `enabled` is
 * deliberately ignored (enforcement is a separate question).
 * Order: the server's `serving_group` stamp → exclude list → include list →
 * Rista account (case / whitespace-insensitive).
 */
export function itemInGroup(item, group) {
  if (!item || !group) return false;
  if (item.serving_group && group.id && item.serving_group === group.id) return true;
  const match = group.match || {};
  const parentSku = String(item.parent_sku || '').trim().toUpperCase();
  if (parentSku && normSkus(match.exclude_parent_skus).includes(parentSku)) return false;
  if (parentSku && normSkus(match.parent_skus).includes(parentSku)) return true;
  const account = normAccount(match.rista_account);
  return !!account && normAccount(item.rista_account) === account;
}

// 660 → "11 AM", 1230 → "8:30 PM" — the compact form for the window label.
function short12h(minutes) {
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  const suffix = hh < 12 ? 'AM' : 'PM';
  const h12 = hh % 12 || 12;
  return mm === 0 ? `${h12} ${suffix}` : `${h12}:${String(mm).padStart(2, '0')} ${suffix}`;
}

/**
 * Today's serving window as customer copy — "Available 11 AM–8 PM", multi-slot
 * "Available 11 AM–2 PM, 5 PM–8 PM". With no slot today, the next opening's
 * label ("Opens tomorrow at 11:00 AM"), else "Not available today".
 */
export function windowLabel(group, now) {
  const n = now || nowInIst();
  const hours = group?.operating_hours || {};
  const spans = [];
  for (const slot of slotsForWeekday(hours, pyWeekday(n))) {
    const o = parseHHMM(slot.open);
    const c = parseHHMM(slot.close);
    if (o == null || c == null || o === c) continue;
    spans.push(`${short12h(o)}–${short12h(c)}`);
  }
  if (spans.length) return `Available ${spans.join(', ')}`;
  const st = storeStatus({ operating_hours: hours, accepting_orders: true }, n);
  return st.opensLabel || 'Not available today';
}

/**
 * Is the group serving right now? Returns
 * `{ open, reason, opensLabel, windowLabel, message }`. A disabled group or one
 * without hours is open (no restriction); the rest delegates to storeStatus on
 * the group's own hours.
 */
export function groupStatus(group, now) {
  const n = now || nowInIst();
  if (group?.enabled === false) {
    return { open: true, reason: DISABLED, opensLabel: null, windowLabel: '', message: '' };
  }
  const hours = group?.operating_hours;
  if (!hours || Object.keys(hours).length === 0) {
    return { open: true, reason: NO_RESTRICTION, opensLabel: null, windowLabel: '', message: '' };
  }
  const st = storeStatus({ operating_hours: hours, accepting_orders: true }, n);
  const label = windowLabel(group, n);
  let message = '';
  if (!st.open) {
    message = String(group.closed_message || '').trim() || GROUP_UNAVAILABLE_MESSAGE;
  }
  return { open: st.open, reason: st.reason, opensLabel: st.opensLabel, windowLabel: label, message };
}

/**
 * The per-item `serving` block for a menu card / product page: the first
 * ENABLED group the item belongs to with its live state, or null when the item
 * is in no group (nothing to show).
 */
export function itemServing(item, groups, now) {
  for (const g of groups || []) {
    if (!g || g.enabled === false) continue;
    if (!itemInGroup(item, g)) continue;
    const st = groupStatus(g, now);
    return {
      open: st.open,
      groupId: g.id,
      groupName: g.name,
      windowLabel: st.windowLabel,
      opensLabel: st.opensLabel,
      message: st.message,
    };
  }
  return null;
}

/**
 * Cart / Checkout: every cart line whose menu row sits in a CLOSED enabled
 * group — `[{menu_item_id, name, groupId, groupName, windowLabel, message}]`,
 * de-duplicated by menu_item_id, cart order. `menuItems` are the full docs
 * from /menu/items/batch (they carry `rista_account` + `parent_sku`).
 */
export function collectClosedGroupLines(cartLines, menuItems, groups, now) {
  const out = [];
  const enabled = (groups || []).filter((g) => g && g.enabled !== false);
  if (!enabled.length) return out;
  const n = now || nowInIst();
  const statuses = new Map();
  const seen = new Set();
  for (const ci of cartLines || []) {
    if (!ci || seen.has(ci.menu_item_id)) continue;
    const mi = (menuItems || []).find((m) => m.id === ci.menu_item_id);
    if (!mi) continue;
    const g = enabled.find((gr) => itemInGroup(mi, gr));
    if (!g) continue;
    if (!statuses.has(g.id)) statuses.set(g.id, groupStatus(g, n));
    const st = statuses.get(g.id);
    if (st.open) continue;
    seen.add(ci.menu_item_id);
    out.push({
      menu_item_id: ci.menu_item_id,
      name: mi.name,
      groupId: g.id,
      groupName: g.name,
      windowLabel: st.windowLabel,
      message: st.message,
    });
  }
  return out;
}

/**
 * The group definitions to evaluate against: the server's `/status` payload
 * (`groups[]`, the authority) when present, else the branch doc's own
 * `product_groups` (instant paint from localStorage).
 */
export function groupsFromStatus(status, fallbackBranch) {
  if (Array.isArray(status?.groups)) return status.groups;
  return Array.isArray(fallbackBranch?.product_groups) ? fallbackBranch.product_groups : [];
}

/**
 * Is the STORE itself open right now? Read it from the `/status` payload when
 * present (it carries the store-level `open`), else compute it client-side from
 * the branch doc's own hours (`storeHours.storeStatus`). Business Rule #52's
 * hide is gated on this: partner-group items are hidden ONLY while the store is
 * open (the morning gap + after the group closes). When the store is closed the
 * store-closed gate (Business Rule #38) owns the whole store, so nothing
 * group-specific happens and items render "the way they do today".
 *
 * Fails to OPEN (returns true) when the store state is unknown, so an
 * indeterminate state can never wrongly HIDE an item — worst case it shows for
 * a beat until `/status` (or the next minute tick) resolves.
 */
export function storeOpenFrom(status, fallbackBranch, now) {
  if (status && typeof status.open === 'boolean') return status.open;
  // No status payload → compute from the branch doc's own hours. A missing/empty
  // branch has no hours, which `storeStatus` reads as legacy-open (fail open) —
  // so an unknown store state never wrongly hides an item.
  const st = storeStatus(fallbackBranch || {}, now || nowInIst());
  return st.open !== false;
}

/**
 * Business Rule #52 — should this menu row be HIDDEN from the customer right
 * now? True only when the STORE is open AND the item belongs to an ENABLED
 * group that is currently outside its window. Client-only rendering helper (the
 * backend never hides — it only refuses the sale), so it lives in this JS mirror
 * alone and is NOT part of the shared open/closed contract with the Python file.
 */
export function isItemGroupHidden(item, groups, now, storeOpen) {
  if (!storeOpen) return false;
  const serving = itemServing(item, groups, now);
  return !!serving && !serving.open;
}

/**
 * Should Checkout refuse the order because of a closed product group?
 * Same contract as storeHours.shouldBlockOrder (Business Rule #38): admins are
 * never blocked, and an UNCONFIRMED status FAILS OPEN — `create_order`
 * re-evaluates the rule and rejects before any payment is taken.
 */
export function shouldBlockGroupLines({ isAdmin = false, statusConfirmed = false, closedLines = [] } = {}) {
  if (isAdmin) return false;
  if (!statusConfirmed) return false;
  return Array.isArray(closedLines) && closedLines.length > 0;
}
